import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Calendar as CalIcon, Plus, Loader2, Save, X, MapPin, Clock, Trash2,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

const COLORS = {
  emerald: { bg: "bg-emerald-500", soft: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cyan:    { bg: "bg-cyan-500",    soft: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  amber:   { bg: "bg-amber-500",   soft: "bg-amber-50 text-amber-700 border-amber-200" },
  rose:    { bg: "bg-rose-500",    soft: "bg-rose-50 text-rose-700 border-rose-200" },
  violet:  { bg: "bg-violet-500",  soft: "bg-violet-50 text-violet-700 border-violet-200" },
};

// Predefined categories — kept in sync with backend `PREDEFINED_CATEGORIES`.
// Each entry maps a stable key to its German label + a default colour for the calendar.
const CATEGORY_PRESETS = [
  { key: "haftpflichtgutachten",  label: "Haftpflichtgutachten", color: "emerald" },
  { key: "kostenvoranschlag",     label: "Kostenvoranschlag",    color: "cyan"    },
  { key: "wertgutachten",         label: "Wertgutachten",        color: "violet"  },
  { key: "anwalt_anrufen",        label: "Anwalt anrufen",       color: "amber"   },
  { key: "versicherung_anrufen",  label: "Versicherung anrufen", color: "rose"    },
  { key: "sonstiges",             label: "Sonstiges",            color: "emerald" },
];

const empty = { title: "", description: "", start_at: "", end_at: "", location: "", color: "emerald", category: "haftpflichtgutachten", custom_category: "" };

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const formatTime = (iso) => iso ? new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "";

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const sameDay = (a, b) => a.toDateString() === b.toDateString();

export default function Termine() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cursor, setCursor] = useState(new Date());
  const [activeDate, setActiveDate] = useState(new Date());
  const [customCategories, setCustomCategories] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments");
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const { data } = await api.get("/appointments/categories");
      setCustomCategories(data.custom || []);
    } catch (_) { /* non-critical */ }
  };

  useEffect(() => { load(); loadCategories(); }, []);

  const openCreate = (date) => {
    setEditing(null);
    const dt = date ? new Date(date) : new Date();
    dt.setHours(10, 0, 0, 0);
    const end = new Date(dt); end.setHours(11, 0, 0, 0);
    setForm({ ...empty, start_at: toLocalInput(dt), end_at: toLocalInput(end) });
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      ...empty, ...a,
      start_at: toLocalInput(a.start_at),
      end_at: toLocalInput(a.end_at),
      category: a.category || "haftpflichtgutachten",
      custom_category: a.custom_category || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.start_at) return toast.error("Startzeit ist Pflichtfeld");
    setSaving(true);
    try {
      // Normalise category: "existing:LABEL" → category="custom", custom_category=LABEL.
      let category = form.category;
      let custom_category = form.custom_category;
      if (typeof category === "string" && category.startsWith("existing:")) {
        custom_category = category.replace("existing:", "");
        category = "custom";
      }
      if (category === "custom" && !custom_category?.trim()) {
        toast.error("Bitte eigene Aufgabe eintragen");
        setSaving(false);
        return;
      }
      // Auto-derive title from category since the standalone Titel field has been removed.
      const preset = CATEGORY_PRESETS.find((p) => p.key === category);
      const title = (form.title?.trim()
                     || (category === "custom" ? custom_category?.trim() : preset?.label)
                     || "Termin");
      const payload = {
        ...form,
        title,
        category,
        custom_category: category === "custom" ? custom_category.trim() : null,
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
      };
      if (editing) {
        await api.patch(`/appointments/${editing.id}`, payload);
        toast.success("Termin aktualisiert");
      } else {
        await api.post("/appointments", payload);
        toast.success("Termin angelegt");
      }
      setOpen(false);
      load();
      loadCategories();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Termin „${a.title}" löschen?`)) return;
    try {
      await api.delete(`/appointments/${a.id}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Calendar grid (6 weeks for stable layout)
  const monthDays = useMemo(() => {
    const first = startOfMonth(cursor);
    const offset = (first.getDay() + 6) % 7; // make Monday = 0
    const start = new Date(first);
    start.setDate(start.getDate() - offset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [cursor]);

  const apptsByDay = useMemo(() => {
    const m = new Map();
    items.forEach((a) => {
      const key = new Date(a.start_at).toDateString();
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(a);
    });
    return m;
  }, [items]);

  const upcoming = useMemo(() => {
    const now = new Date();
    return items
      .filter((a) => new Date(a.start_at) >= new Date(now.toDateString()))
      .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
      .slice(0, 6);
  }, [items]);

  const dayAppts = (apptsByDay.get(activeDate.toDateString()) || []).sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

  return (
    <Layout
      title="Termine"
      subtitle={`${items.length} Termine im Kalender`}
      action={
        <Button onClick={() => openCreate()} data-testid="appt-create-btn">
          <Plus className="h-4 w-4 mr-2" /> Neuer Termin
        </Button>
      }
      primaryAction={{ label: "Neuer Termin", icon: Plus, onClick: () => openCreate(), testid: "mobile-fab-new-appt" }}
    >
      <div className="max-w-7xl space-y-5">
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Calendar */}
          <Card className="lg:col-span-2 p-5" data-testid="appt-calendar">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display text-lg font-bold tracking-tight capitalize">
                  {cursor.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
                </div>
                <div className="text-xs text-zinc-500">Klick auf einen Tag öffnet die Details rechts.</div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="outline" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="appt-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setCursor(new Date()); setActiveDate(new Date()); }}>
                  Heute
                </Button>
                <Button size="icon" variant="outline" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="appt-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-2">
              {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => <div key={d} className="text-center">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {monthDays.map((d) => {
                const inMonth = d.getMonth() === cursor.getMonth();
                const today = sameDay(d, new Date());
                const active = sameDay(d, activeDate);
                const dayItems = apptsByDay.get(d.toDateString()) || [];
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    onClick={() => setActiveDate(d)}
                    onDoubleClick={() => openCreate(d)}
                    className={cn(
                      "relative aspect-square rounded-lg p-1.5 text-left transition border",
                      inMonth ? "bg-white" : "bg-zinc-50/50 text-zinc-400",
                      active ? "border-emerald-500 ring-2 ring-emerald-200" : "border-transparent hover:border-zinc-200",
                    )}
                    data-testid={`appt-day-${d.toISOString().slice(0, 10)}`}
                  >
                    <div className={cn(
                      "text-xs font-semibold inline-flex items-center justify-center h-6 w-6 rounded-full",
                      today ? "bg-emerald-600 text-white" : ""
                    )}>
                      {d.getDate()}
                    </div>
                    {dayItems.length > 0 && (
                      <div className="absolute bottom-1 left-1 right-1 flex gap-0.5 flex-wrap">
                        {dayItems.slice(0, 4).map((a) => (
                          <span key={a.id} className={cn("h-1.5 w-1.5 rounded-full", COLORS[a.color]?.bg || "bg-emerald-500")} />
                        ))}
                        {dayItems.length > 4 && <span className="text-[8px] text-zinc-500 font-bold">+{dayItems.length - 4}</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Day details */}
          <Card className="p-5" data-testid="appt-day-details">
            <div className="font-display text-lg font-bold tracking-tight capitalize">
              {activeDate.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })}
            </div>
            <div className="text-xs text-zinc-500 mb-4">
              {dayAppts.length === 0 ? "Keine Termine an diesem Tag" : `${dayAppts.length} Termin${dayAppts.length === 1 ? "" : "e"}`}
            </div>
            {dayAppts.length === 0 ? (
              <div className="text-center py-8 text-sm text-zinc-400">
                Keine Termine an diesem Tag
              </div>
            ) : (
              <ul className="space-y-2">
                {dayAppts.map((a) => {
                  const cm = COLORS[a.color] || COLORS.emerald;
                  return (
                    <li
                      key={a.id}
                      className="rounded-lg border border-zinc-100 p-3 hover:border-emerald-300 cursor-pointer transition group"
                      onClick={() => openEdit(a)}
                      data-testid={`appt-item-${a.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={cn("w-1.5 self-stretch rounded-full", cm.bg)} />
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-semibold text-sm tracking-tight truncate">{a.title}</div>
                          <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 mt-1">
                            <Clock className="h-3 w-3" /> {formatTime(a.start_at)}{a.end_at ? ` – ${formatTime(a.end_at)}` : ""}
                          </div>
                          {a.location && <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 mt-0.5"><MapPin className="h-3 w-3" /> {a.location}</div>}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); remove(a); }}
                          className="opacity-0 group-hover:opacity-100 transition text-rose-500 hover:bg-rose-50 rounded-md p-1"
                          data-testid={`appt-delete-${a.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {/* "Termin hinzufügen"-Button unten — passt CI an */}
            <Button
              type="button"
              onClick={() => openCreate(activeDate)}
              className="w-full mt-4 brand-gradient-bg text-white"
              data-testid="appt-add-day"
            >
              <Plus className="h-4 w-4 mr-2" /> Termin hinzufügen
            </Button>
          </Card>
        </div>

        {/* Upcoming list — WhatsApp-Style passend zur Akten-Liste */}
        <Card className="p-0 overflow-hidden" data-testid="appt-upcoming">
          <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-zinc-100">
            <div>
              <div className="font-display text-lg font-bold tracking-tight">Anstehende Termine</div>
              <div className="text-xs text-zinc-500">Die nächsten {upcoming.length} Termine</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => openCreate()} data-testid="upcoming-add-btn" className="hidden sm:inline-flex">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Hinzufügen
            </Button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></div>
          ) : upcoming.length === 0 ? (
            <div className="text-sm text-zinc-400 py-10 text-center">Keine anstehenden Termine.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {upcoming.map((a) => {
                const cm = COLORS[a.color] || COLORS.emerald;
                const start = new Date(a.start_at);
                const initials = (a.title || "?")
                  .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
                const today = sameDay(start, new Date());
                const tomorrow = sameDay(start, new Date(Date.now() + 86400000));
                const dateLabel = today
                  ? "Heute"
                  : tomorrow
                  ? "Morgen"
                  : start.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "short" });
                return (
                  <li
                    key={a.id}
                    onClick={() => openEdit(a)}
                    className="flex items-center gap-3 py-3 px-5 hover:bg-zinc-50/70 active:bg-zinc-100/60 cursor-pointer transition-colors"
                    data-testid={`upcoming-item-${a.id}`}
                  >
                    {/* Round colored avatar with initials */}
                    <div className={cn(
                      "h-12 w-12 rounded-full text-white flex items-center justify-center shrink-0 font-display font-bold text-sm shadow-sm",
                      cm.bg
                    )}>
                      {initials || <CalIcon className="h-5 w-5" />}
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-display font-semibold text-[15px] truncate text-zinc-900">{a.title}</div>
                        <span className={cn(
                          "text-[10px] font-bold uppercase tracking-wider shrink-0 px-2 py-0.5 rounded-full border",
                          today ? "bg-emerald-50 text-emerald-700 border-emerald-200" : cm.soft
                        )}>
                          {dateLabel}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <div className="text-[13px] text-zinc-500 truncate flex items-center gap-1">
                          <Clock className="h-3 w-3 shrink-0" /> {formatTime(a.start_at)}
                          {a.end_at && ` – ${formatTime(a.end_at)}`}
                          {a.location && <><span className="mx-1">·</span><MapPin className="h-3 w-3 shrink-0" /> <span className="truncate">{a.location}</span></>}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Termin bearbeiten" : "Neuer Termin"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* ===== Aufgabe ===== */}
            <div data-testid="appt-form-category-section">
              <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Aufgabe</Label>
              <Select
                value={form.category || "haftpflichtgutachten"}
                onValueChange={(v) => {
                  const preset = CATEGORY_PRESETS.find((p) => p.key === v);
                  setForm((prev) => {
                    if (typeof v === "string" && v.startsWith("existing:")) {
                      // user picked a previously-used custom value
                      return {
                        ...prev,
                        category: v,
                        custom_category: v.replace("existing:", ""),
                        title: v.replace("existing:", ""),
                      };
                    }
                    return {
                      ...prev,
                      category: v,
                      custom_category: v === "custom" ? prev.custom_category : "",
                      // Always sync title with the picked preset (Titel-Feld wurde entfernt).
                      title: v !== "custom" && preset ? preset.label : (v === "custom" ? prev.custom_category || "" : prev.title),
                      color: preset ? preset.color : prev.color,
                    };
                  });
                }}
              >
                <SelectTrigger className="mt-1.5" data-testid="appt-form-category">
                  <SelectValue placeholder="Kategorie wählen" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_PRESETS.map((p) => (
                    <SelectItem key={p.key} value={p.key} data-testid={`appt-cat-${p.key}`}>
                      <span className="inline-flex items-center gap-2">
                        <span className={cn("h-2.5 w-2.5 rounded-full", COLORS[p.color]?.bg)} />
                        {p.label}
                      </span>
                    </SelectItem>
                  ))}
                  {customCategories.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Eigene Kategorien</div>
                      {customCategories.slice(0, 20).map((c) => (
                        <SelectItem key={c.key} value={`existing:${c.label}`} data-testid={`appt-cat-existing-${c.key}`}>
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full bg-zinc-400" />
                            {c.label}
                            <span className="text-[10px] text-zinc-400">· {c.usage_count}×</span>
                          </span>
                        </SelectItem>
                      ))}
                    </>
                  )}
                  <SelectItem value="custom" data-testid="appt-cat-custom">
                    <span className="inline-flex items-center gap-2">
                      <Plus className="h-3 w-3 text-emerald-600" /> Eigene Kategorie eingeben…
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {form.category === "custom" && (
                <Input
                  className="mt-2"
                  placeholder='Eigene Aufgabe (z. B. "Werkstatt-Termin")'
                  value={form.custom_category || ""}
                  onChange={(e) => setForm({ ...form, custom_category: e.target.value, title: e.target.value })}
                  data-testid="appt-form-custom-category"
                  maxLength={80}
                />
              )}
              {form.category?.startsWith("existing:") && (
                <div className="text-[11px] text-zinc-500 mt-1.5">
                  Eigene Aufgabe: <strong>{form.category.replace("existing:", "")}</strong>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Start *</Label>
                <Input type="datetime-local" className="mt-1.5" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} data-testid="appt-form-start" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Ende</Label>
                <Input type="datetime-local" className="mt-1.5" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} data-testid="appt-form-end" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Ort</Label>
              <Input className="mt-1.5" value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Adresse oder Ort" data-testid="appt-form-loc" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Notizen</Label>
              <Textarea className="mt-1.5" rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="appt-form-desc" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Farbe</Label>
              <div className="flex gap-2 mt-2">
                {Object.entries(COLORS).map(([k, c]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm({ ...form, color: k })}
                    className={cn("h-7 w-7 rounded-full transition", c.bg, form.color === k && "ring-2 ring-offset-2 ring-zinc-700")}
                    data-testid={`appt-form-color-${k}`}
                    aria-label={k}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}><X className="h-4 w-4 mr-2" /> Abbrechen</Button>
            <Button onClick={save} disabled={saving} data-testid="appt-form-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {editing ? "Speichern" : "Anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
